import { useEffect, useRef, useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

const FuturisticBot = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const mouseRef = useRef({ x: 0, y: 0 });
  const scrollRef = useRef(0);
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Dimensional grid properties
    const grid = {
      rows: isMobile ? 30 : 40,
      cols: isMobile ? 20 : 50,
      nodeSize: 1.5,
      spacing: isMobile ? 25 : 22,
      wavePhase: 0,
      distortionRadius: isMobile ? 0 : 250, // No distortion on mobile
    };

    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      life: number;
      maxLife: number;
      size: number;
    }> = [];

    // Mouse tracking for desktop
    const handleMouseMove = (e: MouseEvent) => {
      if (!isMobile) {
        mouseRef.current.x = e.clientX;
        mouseRef.current.y = e.clientY;
      }
    };

    // Scroll tracking for mobile
    const handleScroll = () => {
      if (isMobile) {
        scrollRef.current = window.scrollY;
      }
    };

    if (!isMobile) {
      window.addEventListener('mousemove', handleMouseMove);
    } else {
      window.addEventListener('scroll', handleScroll);
    }

    const createParticle = (x: number, y: number) => {
      particles.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        life: 60,
        maxLife: 60,
        size: Math.random() * 3 + 1,
      });
    };

    const drawDimensionalGrid = (time: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update wave phase (desktop only)
      if (!isMobile) {
        grid.wavePhase += 0.02;
      }

      // Calculate grid to fill entire screen with overflow
      const extraCols = Math.ceil(canvas.width / grid.spacing) + 4;
      const extraRows = Math.ceil(canvas.height / grid.spacing) + 4;
      const totalCols = Math.max(grid.cols, extraCols);
      const totalRows = Math.max(grid.rows, extraRows);
      const offsetX = -grid.spacing * 2;
      const offsetY = -grid.spacing * 2;

      // Determine distortion center (desktop only)
      let distortionX = canvas.width / 2;
      let distortionY = canvas.height / 2;
      
      if (!isMobile) {
        // Desktop: cursor position
        distortionX = mouseRef.current.x || canvas.width / 2;
        distortionY = mouseRef.current.y || canvas.height / 2;
      }

      // Create grid nodes covering entire screen
      const nodes: Array<{x: number, y: number, z: number, originalX: number, originalY: number}> = [];
      
      // Generate grid nodes with proper screen coverage
      for (let row = 0; row < totalRows; row++) {
        for (let col = 0; col < totalCols; col++) {
          const originalX = offsetX + col * grid.spacing;
          const originalY = offsetY + row * grid.spacing;
          
          let z = 0;
          
          if (!isMobile) {
            // Desktop: cursor-based distortion
            const dx = originalX - distortionX;
            const dy = originalY - distortionY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            const waveIntensity = Math.max(0, 1 - distance / grid.distortionRadius);
            const primaryWave = Math.sin(grid.wavePhase + distance * 0.01) * waveIntensity;
            z = primaryWave * 40;
          }
          // Mobile: completely static, no distortion
          
          // Apply perspective distortion (desktop only)
          let x = originalX;
          let y = originalY;
          
          if (!isMobile && z !== 0) {
            const perspective = 1000;
            const scale = perspective / (perspective + z);
            const perspectiveShift = (scale - 1) * 0.3;
            x = originalX + (originalX - canvas.width / 2) * perspectiveShift;
            y = originalY + (originalY - canvas.height / 2) * perspectiveShift;
          }
          
          nodes.push({ x, y, z, originalX, originalY });
        }
      }
      
      // Draw connections with static styling
      ctx.strokeStyle = 'hsla(228, 100%, 70%, 0.08)';
      ctx.lineWidth = 0.8;
      
      for (let row = 0; row < totalRows; row++) {
        for (let col = 0; col < totalCols; col++) {
          const currentIndex = row * totalCols + col;
          const current = nodes[currentIndex];
          
          // Horizontal connections
          if (col < totalCols - 1) {
            const rightIndex = row * totalCols + (col + 1);
            const right = nodes[rightIndex];
            
            const alpha = isMobile ? 0.1 : Math.max(0.02, 0.15 - Math.abs(current.z + right.z) * 0.005);
            ctx.strokeStyle = `hsla(228, 100%, 70%, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(current.x, current.y);
            ctx.lineTo(right.x, right.y);
            ctx.stroke();
          }
          
          // Vertical connections  
          if (row < totalRows - 1) {
            const bottomIndex = (row + 1) * totalCols + col;
            const bottom = nodes[bottomIndex];
            
            const alpha = isMobile ? 0.08 : Math.max(0.02, 0.15 - Math.abs(current.z + bottom.z) * 0.005);
            ctx.strokeStyle = `hsla(278, 100%, 70%, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(current.x, current.y);
            ctx.lineTo(bottom.x, bottom.y);
            ctx.stroke();
          }
        }
      }

      // Draw grid nodes with simple styling
      nodes.forEach((node, index) => {
        let alpha = 0.4;
        let nodeSize = grid.nodeSize;
        let hue = 228;
        let brightness = 70;
        let saturation = 80;
        
        if (!isMobile) {
          // Desktop: dynamic styling based on distortion
          const distanceFromCenter = Math.sqrt(
            Math.pow(node.originalX - distortionX, 2) + 
            Math.pow(node.originalY - distortionY, 2)
          );
          
          const intensity = Math.max(0.05, 1 - distanceFromCenter / grid.distortionRadius);
          nodeSize = grid.nodeSize + Math.abs(node.z) * 0.03;
          alpha = Math.max(0.1, intensity * 0.8);
          
          const depthFactor = Math.abs(node.z) / 40;
          hue = node.z > 0 ? 278 + depthFactor * 20 : 228 - depthFactor * 15;
          brightness = 60 + depthFactor * 30;
          saturation = 80 + depthFactor * 20;
        }
        
        ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${brightness}%, ${alpha})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, nodeSize, 0, Math.PI * 2);
        ctx.fill();
        
        // Add glow effect for prominent nodes (desktop only)
        if (!isMobile && alpha > 0.3 && Math.abs(node.z) > 20) {
          ctx.shadowColor = `hsla(${hue}, 100%, 80%, 0.3)`;
          ctx.shadowBlur = nodeSize * 3;
          ctx.beginPath();
          ctx.arc(node.x, node.y, nodeSize * 0.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
      });

      // No particles on mobile, minimal particles on desktop
      if (!isMobile && Math.random() < 0.02) {
        const spawnRadius = grid.distortionRadius * 0.7;
        createParticle(
          distortionX + (Math.random() - 0.5) * spawnRadius,
          distortionY + (Math.random() - 0.5) * spawnRadius
        );
      }

      // Update and draw particles (desktop only)
      if (!isMobile) {
        for (let i = particles.length - 1; i >= 0; i--) {
          const particle = particles[i];
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.life--;

          if (particle.life <= 0) {
            particles.splice(i, 1);
            continue;
          }

          const alpha = particle.life / particle.maxLife;
          ctx.fillStyle = `hsla(250, 100%, 80%, ${alpha * 0.6})`;
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size * alpha, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    };

    const animate = (time: number) => {
      drawDimensionalGrid(time);
      animationRef.current = requestAnimationFrame(animate);
    };

    animate(0);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resizeCanvas);
      if (!isMobile) {
        window.removeEventListener('mousemove', handleMouseMove);
      } else {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [mounted, isMobile]);

  if (!mounted) {
    return <div className="absolute inset-0 bg-gradient-to-br from-background/90 to-background/60" />;
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ 
        background: 'transparent',
        filter: 'blur(0.5px)',
        opacity: 0.85
      }}
    />
  );
};

export default FuturisticBot;